import { rtkApi } from '../../rtkApi';

export interface RateAdjustments {
  creditScore: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  ltv: {
    low: number;
    moderate: number;
    high: number;
    veryHigh: number;
  };
}

export interface RateMatrixPayload {
  rowLabels: string[];
  columnLabels: string[];
  values: number[][];
  lastUpdated: string | null;
}

export interface AprAssumptions {
  originationPercent: number;
  pointsPercent: number;
  underwritingFee: number;
  processingFee: number;
  applicationFee: number;
  creditReportFee: number;
  lenderFees: number;
  otherFees: number;
  prepaidInterestDays: number;
  fhaUfmipPercent: number;
  fhaAnnualMIPPercent: number;
  vaFundingFeePercent: number;
  usdaUpfrontGuaranteeFeePercent: number;
  usdaAnnualFeePercent: number;
}

export type FeeCategory =
  | 'lender'
  | 'title'
  | 'government'
  | 'prepaid'
  | 'other';
export type FeeSign = 'add' | 'subtract';
export type FeeAmountType = 'flat' | 'percentOfLoan';

export interface FeeItem {
  key: string;
  label: string;
  description?: string;
  category: FeeCategory;
  sign: FeeSign;
  amountType: FeeAmountType;
  amount: number;
  includedInApr: boolean;
}

export interface CalculatedFeeRule {
  key: string;
  label: string;
  description?: string;
  category: FeeCategory;
  visible: boolean;
  includedInApr: boolean;
}

export interface RateProgram {
  label: string;
  baseRate: number;
  adjustments: RateAdjustments;
  matrix: RateMatrixPayload;
  aprAssumptions?: AprAssumptions;
  feeItems?: FeeItem[];
  calculatedFeeRules?: CalculatedFeeRule[];
  refinanceDocs?: Record<string, string[]>;
  documentationRequests?: Record<string, Record<string, string[]>>;
}

export interface BridgeTerms {
  sixMonth: number;
  twelveMonth: number;
  eighteenMonth: number;
  twentyFourMonth: number;
  baseRate: number;
  indexType: string;
  indexValue: number;
  spread: number;
}

export interface InterestRateMetadata {
  notes: string;
  updatedBy: string;
  reason: string;
  publishedAt: string | null;
}

export interface InterestRate {
  _id: string;
  status: 'draft' | 'active';
  version: number;
  programs: {
    cash: RateProgram;
    conventional: RateProgram;
    fha: RateProgram;
    va: RateProgram;
    usda: RateProgram;
    dscr: RateProgram;
    commercial: RateProgram;
    fixAndFlip: RateProgram;
    bridge: RateProgram;
    heloc: RateProgram;
    heloan: RateProgram;
    secondMortgage: RateProgram;
  };
  bridgeTerms: BridgeTerms;
  metadata: InterestRateMetadata;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RateChange {
  _id: string;
  rateConfigId: string;
  program: string;
  fieldPath: string;
  before: unknown;
  after: unknown;
  reason: string;
  changedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  stats: Record<string, unknown> & {
    lastUpdated: string;
    totalChanges: number;
  };
  currentRates: InterestRate;
  recentChanges: RateChange[];
}

export interface CreateInterestRateRequest {
  programs?: Partial<InterestRate['programs']>;
  bridgeTerms?: Partial<BridgeTerms>;
  notes?: string;
  reason?: string;
  updatedBy?: string;
}

export interface PatchBaseRateRequest {
  configId: string;
  program: string;
  baseRate: number;
  reason?: string;
}

export interface PatchProgramMatrixRequest {
  configId: string;
  program: string;
  rowIndex?: number;
  columnIndex?: number;
  value?: number;
  values?: number[][];
  aprAssumptions?: Partial<AprAssumptions>;
  feeItems?: FeeItem[];
  calculatedFeeRules?: CalculatedFeeRule[];
  refinanceDocs?: Record<string, string[]>;
  documentationRequests?: Record<string, Record<string, string[]>>;
  reason?: string;
}

export interface PatchBridgeTermsRequest {
  configId: string;
  updates: Partial<BridgeTerms>;
  reason?: string;
}

export interface AdjustInterestRateRequest {
  program: string;
  adjustment: number;
}

export const interestRateApi = rtkApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getLatestRates: builder.query<InterestRate, void>({
      query: () => ({
        url: '/admin/interest-rates/latest',
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: InterestRate }) =>
        response.data,
      providesTags: ['InterestRates'],
    }),

    getRateHistory: builder.query<RateChange[], { limit?: number }>({
      query: ({ limit = 30 }) => ({
        url: `/admin/interest-rates/history?limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: RateChange[] }) =>
        response.data,
      providesTags: ['InterestRateHistory'],
    }),

    getConfigurationHistory: builder.query<
      RateChange[],
      { id: string; limit?: number }
    >({
      query: ({ id, limit = 50 }) => ({
        url: `/admin/interest-rates/${id}/history?limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: RateChange[] }) =>
        response.data,
      providesTags: ['InterestRateHistory'],
    }),

    createInterestRate: builder.mutation<
      InterestRate,
      CreateInterestRateRequest
    >({
      query: (data) => ({
        url: '/admin/interest-rates',
        method: 'POST',
        data: data,
      }),
      transformResponse: (response: { success: boolean; data: InterestRate }) =>
        response.data,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),

    getDashboardStats: builder.query<DashboardStats, void>({
      query: () => ({
        url: '/admin/interest-rates/dashboard',
        method: 'GET',
      }),
      transformResponse: (response: {
        success: boolean;
        data: DashboardStats;
      }) => response.data,
      providesTags: ['InterestRates', 'InterestRateHistory'],
    }),

    patchProgramBaseRate: builder.mutation<InterestRate, PatchBaseRateRequest>({
      query: ({ configId, program, baseRate, reason }) => ({
        url: `/admin/interest-rates/${configId}/programs/${program}/base-rate`,
        method: 'PATCH',
        data: { baseRate, reason },
      }),
      transformResponse: (response: { success: boolean; data: InterestRate }) =>
        response.data,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),

    patchProgramMatrixCell: builder.mutation<
      InterestRate,
      PatchProgramMatrixRequest
    >({
      query: ({
        configId,
        program,
        rowIndex,
        columnIndex,
        value,
        values,
        aprAssumptions,
        feeItems,
        calculatedFeeRules,
        refinanceDocs,
        documentationRequests,
        reason,
      }) => {
        // If values array is provided, use it for full matrix update
        if (
          values !== undefined &&
          values !== null &&
          Array.isArray(values) &&
          values.length > 0
        ) {
          // Ensure it's a proper 2D array
          if (!values.every((row) => Array.isArray(row))) {
            throw new Error('values must be a 2D array (array of arrays)');
          }

          console.log('Sending matrix update request:', {
            configId,
            program,
            valuesLength: values.length,
            firstRowLength: values[0]?.length,
            hasReason: !!reason,
          });

          return {
            url: `/admin/interest-rates/${configId}/programs/${program}/matrix`,
            method: 'PATCH',
            data: {
              values,
              aprAssumptions,
              feeItems,
              calculatedFeeRules,
              refinanceDocs,
              documentationRequests,
              reason,
            },
          };
        }

        // Otherwise, use single cell update
        if (
          rowIndex !== undefined &&
          columnIndex !== undefined &&
          value !== undefined
        ) {
          return {
            url: `/admin/interest-rates/${configId}/programs/${program}/matrix`,
            method: 'PATCH',
            data: {
              rowIndex,
              columnIndex,
              value,
              aprAssumptions,
              feeItems,
              calculatedFeeRules,
              refinanceDocs,
              reason,
            },
          };
        }

        throw new Error(
          'Either values array or rowIndex, columnIndex, and value must be provided'
        );
      },
      transformResponse: (response: { success: boolean; data: InterestRate }) =>
        response.data,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),

    patchBridgeTerms: builder.mutation<BridgeTerms, PatchBridgeTermsRequest>({
      query: ({ configId, updates, reason }) => ({
        url: `/admin/interest-rates/${configId}/bridge-terms`,
        method: 'PATCH',
        data: { updates, reason },
      }),
      transformResponse: (response: { success: boolean; data: BridgeTerms }) =>
        response.data,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),

    adjustInterestRate: builder.mutation<
      InterestRate,
      AdjustInterestRateRequest
    >({
      query: ({ program, adjustment }) => ({
        url: '/admin/interest-rates/adjust',
        method: 'POST',
        data: { program, adjustment },
      }),
      transformResponse: (response: { success: boolean; data: InterestRate }) =>
        response.data,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),

    deleteInterestRate: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (id) => ({
        url: `/admin/interest-rates/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { success: boolean; message: string }) =>
        response,
      invalidatesTags: [
        'InterestRates',
        'InterestRateHistory',
        'UserInterestRates',
      ],
    }),
  }),
});

export const {
  useGetLatestRatesQuery,
  useGetRateHistoryQuery,
  useGetConfigurationHistoryQuery,
  useCreateInterestRateMutation,
  useGetDashboardStatsQuery,
  usePatchProgramBaseRateMutation,
  usePatchProgramMatrixCellMutation,
  usePatchBridgeTermsMutation,
  useAdjustInterestRateMutation,
  useDeleteInterestRateMutation,
} = interestRateApi;
